import { createFileRoute } from "@tanstack/react-router";
import { doc, serverTimestamp, updateDoc, type Timestamp } from "firebase/firestore";
import { Inbox, Loader2, Mail, RotateCcw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { useConfirm } from "@/components/DialogProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { db } from "@/firebase";
import type { UserModel } from "@/firestore_types";
import { useDeletedUsers } from "@/features/users/hooks/useUsers";

export const Route = createFileRoute("/dashboard/admin/trash/users")({
  component: RouteComponent,
});

function RouteComponent() {
  const { deletedUsers } = useDeletedUsers();

  return (
    <div className="flex flex-col gap-6">
      {deletedUsers.length === 0 ? (
        <EmptyState />
      ) : (
        deletedUsers.map((user) => <UserCard key={user.uid} user={user} />)
      )}
    </div>
  );
}

interface UserCardProps {
  user: UserModel;
}

function UserCard({ user }: UserCardProps) {
  const { uid, name, email, type, deletedAt } = user;

  const deletedDate = formatDateLabel(deletedAt ?? null);

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            {name}
            <Badge variant="secondary" className="capitalize">
              {type}
            </Badge>
          </CardTitle>
          <CardDescription className="flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-2">
              <Mail className="text-muted-foreground size-4" />
              {email}
            </span>
            {deletedDate && (
              <span className="text-muted-foreground text-xs">Deleted on {deletedDate}</span>
            )}
          </CardDescription>
        </div>
        <CardAction>
          <RestoreUserButton userId={uid} userName={name} />
        </CardAction>
      </CardHeader>
      <CardFooter className="text-muted-foreground flex-col items-start gap-2 text-xs md:flex-row md:items-center md:justify-between">
        <p>Restoring a user will make them visible again and restore their access.</p>
      </CardFooter>
    </Card>
  );
}

interface RestoreUserButtonProps {
  userId: string;
  userName: string;
}

function RestoreUserButton({ userId, userName }: RestoreUserButtonProps) {
  const [isRestoring, setIsRestoring] = useState(false);

  const confirm = useConfirm();

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore user",
      description: `"${userName}" will regain access and be visible in the system again.`,
    });

    if (!confirmed) return;

    setIsRestoring(true);
    try {
      const userRef = doc(db, "Users", userId);
      await updateDoc(userRef, {
        isDeleted: false,
        deletedAt: null,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Restored "${userName}"`);
    } catch (error) {
      console.error("Failed to restore user", error);
      toast.error("Something went wrong while restoring the user.");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <Button onClick={handleRestore} size="sm" variant="outline" disabled={isRestoring}>
      {isRestoring ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Restoring...
        </>
      ) : (
        <>
          <RotateCcw className="size-4" />
          Restore
        </>
      )}
    </Button>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted users</p>
        <p className="text-muted-foreground text-sm">
          Deleted users will appear here for quick recovery.
        </p>
      </div>
    </div>
  );
}

function formatDateLabel(value: Timestamp | null) {
  if (!value) return null;
  const date = value.toDate();
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
